import React, { useState, useRef } from "react";
import Cropper from "react-easy-crop/umd/react-easy-crop.js";
import {
  Button,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import BrandAmbassadorAgreement from "components/BrandAmbassadorAgreement";
import fhpPolicyImg from "../../assets/img/custom/FHPPolicy.jpg";

const RegisterModals = ({
  // Notice modal
  noticeOpen,
  setNoticeOpen,
  noticeChecked,
  setNoticeChecked,
  setNoticeAccepted,

  // Image upload modal
  imgOpen,
  imgToggle,
  setImage,
  imagePreview: parentImagePreview,
  setImagePreview: setParentImagePreview,

  // Image note modal
  noteToggle,
  toggleNote,
  photoInstruction,
  setPhotoInstruction,

  // Tax modal
  taxOpen,
  taxToggle,
  setTax,

  // FHP Policy modal
  policyModalOpen,
  PolicyToggle,

  // Agreement modal
  agrOpen,
  agreeToggle,
  setAgreement,

  // Terms of License modal
  tolOpen,
  tolToggle,
  setTol,

  // TOL info modal
  tolOpenInfo,
  tolInfoToggle,

  // Brand Ambassador Agreement
  ambassadorAgreement,
  ambassdorToggle,
  agreementBox,
  setAgreementBox,
  selectedClause,
  setSelectedClause,

  // High School athlete info modal
  highSchoolAthleteInfo,
  highSchoolAthleteInfoToggle,

  // State law modal
  lawModal,
  toggleLawModal,
  lawData,

  // Terms and Conditions modal
  tacModalOpen,
  tacModalToggle,
  setTac,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(parentImagePreview || null);
  const [isHovering, setIsHovering] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (1MB = 1048576 bytes)
      if (file.size > 1048576) {
        setSizeError(true);
        setSelectedImage(null);
        setImagePreview(null);
        setParentImagePreview("");
        // Reset the file input
        e.target.value = "";
        return;
      }
      
      setSizeError(false);
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setParentImagePreview("");
    setSizeError(false);
    // Reset the file input
    const fileInput = document.querySelector('input[name="player_img"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    });
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (selectedImage && imagePreview && croppedAreaPixels) {
      try {
        const croppedBlob = await getCroppedImg(
          imagePreview,
          croppedAreaPixels
        );
        
        const croppedFile = new File([croppedBlob], selectedImage.name, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });

        setImage(croppedFile);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setParentImagePreview(reader.result);
        };
        reader.readAsDataURL(croppedFile);

        toast.success("Image cropped and uploaded successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        imgToggle();
      } catch (err) {
        console.error("Failed to crop image:", err);
        toast.error("Failed to crop image. Please try again.");
      }
    } else if (selectedImage) {
      setImage(selectedImage);
      setParentImagePreview(imagePreview);
      toast.success("Image uploaded successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      imgToggle();
    }
  };

  return (
    <>
      {/* Website Availability Notice Modal */}
      <Modal
        isOpen={noticeOpen}
        toggle={() => setNoticeOpen(!noticeOpen)}
        size="md"
      >
        <ModalHeader>
          Website Availability and Limitation of Liability Notice
        </ModalHeader>
        <ModalBody>
          <p>
            FansHelpPlayers, LLC strives to maintain reliable and continuous
            operation of this website and its associated services. However, due
            to the complexity of the platform, technical issues, maintenance
            activities, third-party dependencies, or unforeseen circumstances,
            interruptions, delays, errors, or temporary unavailability may
            occasionally occur.
          </p>
          <p>
            By using this website, you acknowledge and agree that
            FansHelpPlayers makes no guarantees regarding uninterrupted access,
            error-free operation, or availability. FansHelpPlayers assumes no
            liability for any loss, damage, or inconvenience arising from the
            website being unavailable or not functioning as intended.
          </p>
          <p>
            FansHelpPlayers will make commercially reasonable, good-faith
            efforts to maintain and restore site functionality and to address
            issues as they arise, but use of the site is at your own risk.
          </p>
          <p>
            This notice applies to all users and all services provided through
            the FansHelpPlayers website.
          </p>
        </ModalBody>
        <ModalFooter className="d-flex flex-column align-items-start">
          <div className="form-check mb-2">
            <Input
              className="form-check-input"
              type="checkbox"
              id="noticeCheckbox"
              checked={noticeChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                setNoticeChecked(checked);
                setNoticeAccepted(checked);
                if (checked) {
                  setNoticeOpen(false);
                }
              }}
              style={{
                accentColor: noticeChecked ? "#007bff" : "#ff4d4d",
                width: "18px",
                height: "18px",
                cursor: "pointer",
              }}
            />
            <Label
              className="form-check-label"
              htmlFor="noticeCheckbox"
              style={{
                color: noticeChecked ? "#007bff" : "#ff4d4d",
                marginLeft: "8px",
                cursor: "pointer",
              }}
            >
              I have read and understand this notice.
            </Label>
          </div>
          <div className="d-flex justify-content-end w-100 mt-2">
            <Button
              color="secondary"
              onClick={() => {
                setNoticeAccepted(noticeChecked);
                setNoticeOpen(false);
              }}
            >
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Upload Photo Modal */}
      <Modal isOpen={imgOpen}>
        <ModalBody>
          <Form onSubmit={handleImageUpload}>
            <Label className="custom-label ">
              <p className="text-dark">
                Make sure the photo you upload shows your full face clearly, so
                your fans can recognize you. Make sure the photo you upload
                complies to your school's policy for NIL activities of amateur
                athletes.
              </p>
            </Label>
            <br />
            <Label className="auth-label mt-4">Upload Image</Label>
            <Input
              type="file"
              required
              name="player_img"
              accept="image/png, image/gif, image/jpeg"
              onChange={handleImageChange}
            />
            
            {/* Show size error only when violation occurs */}
            {sizeError && (
              <Label className="auth-label text-danger">
                Max Size 1MB - Please select a smaller image
              </Label>
            )}

            {/* Image Preview and Crop Section */}
            {imagePreview && (
              <div className="d-flex flex-column align-items-center mt-3" style={{ width: "100%" }}>
                <div className="position-relative" style={{ width: "100%", height: "280px", background: "#1e293b", borderRadius: "12px", overflow: "hidden" }}>
                  <Cropper
                    image={imagePreview}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(croppedArea, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                  />
                </div>
                
                <div className="mt-3 w-100 d-flex align-items-center gap-3">
                  <span style={{ fontSize: "0.8125rem", color: "#475569", fontWeight: 600 }}>Zoom</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            )}

            <div className="text-right mt-4">
              <Button color="danger" onClick={imgToggle} className="mr-2">
                Close
              </Button>
              <Button color="primary" type="submit" disabled={!selectedImage}>
                Upload
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Photo Instructions Note Modal */}
      <Modal isOpen={noteToggle.imgNote}>
        <ModalBody>
          <strong>
            <u>Before posting your photo: </u>
          </strong>
          <Label className="custom-label text-dark">
            <p className="text-dark">
              Some schools do not allow you to wear your uniform during NIL
              activities or show school facilities in the photo. Find out your
              school's policy first, before posting your photo.
            </p>
          </Label>
          <div className="d-flex mt-2 ml-4">
            <Input
              required
              type="checkbox"
              className="custom-checkbox"
              checked={photoInstruction}
              onChange={(e) => setPhotoInstruction(e.target.checked)}
            />
            <Label className="custom-checkbox-label text-dark mx-4 mt-1">
              I've read the photo instructions.
            </Label>
          </div>
          <div className="text-right mt-2">
            <Button
              color="danger"
              className="mr-2"
              onClick={() => toggleNote("imgNote")}
            >
              Close
            </Button>
            <Button
              disabled={!photoInstruction}
              color="primary"
              onClick={() => {
                toggleNote("imgNote");
                imgToggle();
              }}
            >
              Continue
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Tax Responsibility Modal */}
      <Modal isOpen={taxOpen}>
        <ModalBody>
          <Label className="custom-label text-danger">
            <b>Warning:</b>
          </Label>
          <Label className="custom-label">
            <p className="text-dark">
              I understand and agree that FansHelpPlayers.com is not responsible
              to provide any registered amateur athlete with assistance on tax
              preparation, tax payment or tax advice. I understand that money I
              receive from Fans via FansHelpPlayers.com is subject to the
              possibility of state and/or federal taxes. I understand and agree
              that I, and not FansHelpPlayers.com, am responsible to save money
              to cover the tax. I understand and agree that if and when required
              to account by the tax authority, I and not FansHelpPlayers.com, am
              responsible to make an accounting of monies I may have received
              via FansHelpPlayers.com.
            </p>
          </Label>
          <div className="text-right">
            <Button
              color="primary"
              onClick={() => {
                setTax(true);
                taxToggle();
              }}
            >
              Agree
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* FHP Policy Modal */}
      <Modal isOpen={policyModalOpen} toggle={PolicyToggle} size="lg">
        <ModalBody>
          <img src={fhpPolicyImg} style={{ width: "100%" }} alt="FHP Policy" />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={PolicyToggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Limited Registration Term Modal */}
      <Modal isOpen={agrOpen}>
        <ModalBody>
          <p className="custom-checkbox-label text-dark">
            I understand and agree that my registration shall be for a term up
            to a maximum of my years of school enrollment.
          </p>
          <div className="text-right">
            <Button
              color="primary"
              onClick={() => {
                setAgreement(true);
                agreeToggle();
              }}
            >
              Agree
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Terms of License Modal */}
      <Modal isOpen={tolOpen} toggle={tolToggle} size="lg">
        <div className="d-flex justify-content-between align-items-center p-3 pointer">
          <div>License Agreement</div>
        </div>
        <ModalBody>
          <Label className="custom-label">
            <strong className="text-dark">
              B0TH FAN BUYER AND ATHLETE SELLER AGREE TO TERMS OF USE BELOW:
            </strong>{" "}
            <br />
            <br />
            <b className="text-dark">1. FAN SEARCH FOR PLAYER:</b>{" "}
            <p className="text-dark">
              After entering site, Fan may visit Search Page and search for at
              least one Amateur Athlete from whom Fan might then decide to
              purchase a license to use said Athlete's Name and photo. Then Fan
              selects a license price.{" "}
            </p>{" "}
            <b className="text-dark">2. PRICE OF LICENSE:</b>{" "}
            <p className="text-dark">
              Fan Buyer is free to choose price for license from range of $4 to
              $599.{" "}
            </p>{" "}
            <b className="text-dark">3. GRANT OF LICENSE:</b>{" "}
            <p className="text-dark">
              Upon payment by Fan buyer to Athlete seller of Fan-selected price,
              buyer is granted use-of-name license by athlete-seller per terms
              herein disclosed. Fan-buyer thus will be enabled to download a
              badge of endorsement upon which is listed: Name of Fan Buyer as
              endorsed in Team Spirit by Athlete selected by Fan, plus name and
              photo of endorsing Athlete, date of issuance of badge and price
              for badge which Fan chose to pay.{" "}
            </p>{" "}
            <b className="text-dark">4. USE OF BADGE:</b>
            <p className="text-dark">
              Fan Buyer is authorized to display badge on Fan Buyer's social
              media pages during time when license is current.{" "}
            </p>{" "}
            <b className="text-dark">5. EXPIRATION OF LICENSE:</b>{" "}
            <p className="text-dark">
              The license will expire after the number of weeks equal to the
              number of dollars paid for badge. Example: A badge for which Fan
              Buyer chose to pay $4 expires after four weeks.
            </p>{" "}
            <b className="text-dark">6. RULES OF AMATEUR ATHLETICS:</b>{" "}
            <p className="text-dark">
              Athlete seller is solely responsible to verify before he/she
              undertakes this license sale, that this license sale and
              associated financial transaction qualifies under applicable rules
              as allowing said athlete to maintain his/her amateur status.
            </p>
          </Label>
          <div className="text-right">
            <Button
              color="primary"
              onClick={() => {
                setTol(true);
                tolToggle();
              }}
            >
              Agree
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* TOL Info Modal */}
      <Modal isOpen={tolOpenInfo} toggle={tolInfoToggle} size="lg">
        <ModalBody>
          <Label className="custom-label">
            <b className="text-dark" style={{ textDecoration: "underline" }}>
              Note:{" "}
            </b>
            Users of FHP should familiarize themselves with the contents of this
            video: ____________________ . Please share this link with all fans
            of school sports and all student athletes.
          </Label>
        </ModalBody>
      </Modal>

      {/* Brand Ambassador Agreement */}
      <BrandAmbassadorAgreement
        isOpen={ambassadorAgreement}
        toggle={ambassdorToggle}
        setAgreementBox={setAgreementBox}
        agreementBox={agreementBox}
        selectedClause={selectedClause}
        setSelectedClause={setSelectedClause}
      />

      {/* High School Athlete Info Modal */}
      <Modal
        isOpen={highSchoolAthleteInfo}
        toggle={highSchoolAthleteInfoToggle}
        size="lg"
      >
        <Container>
          <h3 className="mx-4 my-3">
            Disclaimer: information on this site is made available for
            educational purposes. It provides general Information and is not
            Intended to provide specific legal advice. This information should
            not be used as a substitute for competent legal advice from a{" "}
            <span className="text-underline">
              licensed attorney in your jurisdiction.
            </span>
          </h3>
          <ol className="ml-2 mr-5">
            <li>
              <h4>
                Laws regarding NIL can vary significantly state by state. States
                may change their NIL laws.
              </h4>
            </li>
            <li>
              <h4>
                Do Not assume, Just because an athlete whose high school Is In a
                particular state where FHP allows registration, do not assume
                that means said state allows all specific types of NIL activity.
                Athletes must also check with their advisors as to which
                activities the athlete should use at FHP functions. Specifically,
                It Is useful to review which chat or video or photo choices to
                make or avoid.
              </h4>
            </li>
          </ol>
        </Container>
        <div className="d-flex justify-content-end align-items-end mb-2 mr-4">
          <Button color="primary" onClick={highSchoolAthleteInfoToggle}>
            Noted
          </Button>
        </div>
      </Modal>

      {/* State Law Modal */}
      <Modal isOpen={lawModal} toggle={toggleLawModal} size="lg">
        <Container className="py-4">
          <h1 className="text-center">{lawData?.limit?.article}</h1>
          <b>{lawData?.limit?.text}</b>
          <div className="text-right">
            <Button onClick={toggleLawModal} size="sm" color="primary">
              Ok
            </Button>
          </div>
        </Container>
      </Modal>

      {/* Terms and Conditions Modal */}
      <Modal isOpen={tacModalOpen} toggle={tacModalToggle} size="xl">
        <ModalHeader toggle={tacModalToggle}>
          Terms and Conditions
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <iframe
            src="/terms-and-conditions?register"
            style={{
              width: "100%",
              height: "60vh",
              border: "none",
            }}
            title="Terms and Conditions"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => {
            if (setTac) setTac(true);
            tacModalToggle();
          }}>
            I Agree
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RegisterModals;
